// Stratus brand mark — an animated "cloud of synapses": glowing nodes scattered
// inside a cloud silhouette, linked as drifting neural edges (Stratus = cloud).
// Canvas-rendered, self-contained (no shipped asset). Honors prefers-reduced-motion
// by drawing a single static frame. The cyan glow is the brand accent and is
// intentionally theme-independent.

import { useEffect, useRef } from 'react';

// Lobes fill the canvas (centered ~0.5) so the cloud reads large at small sizes.
const LOBES = [
  { x: 0.50, y: 0.52, r: 0.300 }, { x: 0.29, y: 0.56, r: 0.220 }, { x: 0.71, y: 0.56, r: 0.220 },
  { x: 0.40, y: 0.38, r: 0.210 }, { x: 0.61, y: 0.40, r: 0.190 }, { x: 0.50, y: 0.60, r: 0.280 },
];
const PALETTE = ['#22d3ee', '#38bdf8', '#60a5fa', '#818cf8', '#a78bfa', '#2dd4bf', '#e0f2fe'];

function inside(x: number, y: number) {
  return LOBES.some(l => { const dx = x - l.x, dy = y - l.y; return dx * dx + dy * dy < l.r * l.r; });
}

type Node = { bx: number; by: number; ph: number; ax: number; ay: number; col: string; sz: number };

function build() {
  const nodes: Node[] = []; let tries = 0;
  while (nodes.length < 24 && tries < 6000) {
    tries++;
    const x = 0.07 + Math.random() * 0.86, y = 0.18 + Math.random() * 0.64;
    if (!inside(x, y)) continue;
    if (nodes.every(n => { const dx = n.bx - x, dy = n.by - y; return dx * dx + dy * dy > 0.0055; })) {
      nodes.push({
        bx: x, by: y, ph: Math.random() * 6.283,
        ax: 0.020 + Math.random() * 0.028, ay: 0.020 + Math.random() * 0.028,
        col: PALETTE[(Math.random() * PALETTE.length) | 0], sz: 0.9 + Math.random() * 1.3,
      });
    }
  }
  const edges: Array<[number, number, number]> = [];
  for (let a = 0; a < nodes.length; a++) for (let b = a + 1; b < nodes.length; b++) {
    const dx = nodes[a].bx - nodes[b].bx, dy = nodes[a].by - nodes[b].by, d = Math.sqrt(dx * dx + dy * dy);
    if (d < 0.22) edges.push([a, b, d]);
  }
  return { nodes, edges };
}

export function StratusLogo({ size = 32, withText = true, className = '' }: { size?: number; withText?: boolean; className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const DPR = Math.min(2, window.devicePixelRatio || 1);
    const W = size * DPR, H = size * DPR;
    cv.width = W; cv.height = H;
    const { nodes, edges } = build();
    // The brand mark always animates (owner request) — no reduced-motion freeze.
    let raf = 0, t = 0;
    const draw = () => {
      t += 0.025;
      ctx.clearRect(0, 0, W, H);
      const pulse = 1 + 0.14 * Math.sin(t * 1.6);
      const cx = W * 0.5, cy = H * 0.50, R = W * 0.54 * pulse;
      const g = ctx.createRadialGradient(cx, cy - H * 0.02, 0, cx, cy, R);
      const halo = 0.26 + 0.10 * Math.sin(t * 1.6);
      g.addColorStop(0, `rgba(56,189,248,${halo.toFixed(3)})`); g.addColorStop(0.5, 'rgba(56,189,248,0.09)'); g.addColorStop(1, 'rgba(56,189,248,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      ctx.save(); ctx.globalCompositeOperation = 'lighter';
      for (const l of LOBES) {
        const lg = ctx.createRadialGradient(l.x * W, l.y * H, 0, l.x * W, l.y * H, l.r * W);
        lg.addColorStop(0, 'rgba(45,120,190,0.14)'); lg.addColorStop(1, 'rgba(45,120,190,0)');
        ctx.fillStyle = lg; ctx.beginPath(); ctx.arc(l.x * W, l.y * H, l.r * W, 0, 6.2832); ctx.fill();
      }
      ctx.restore();
      const P = nodes.map(nd => [(nd.bx + Math.sin(t + nd.ph) * nd.ax) * W, (nd.by + Math.cos(t * 0.9 + nd.ph) * nd.ay) * H] as [number, number]);
      ctx.save(); ctx.globalCompositeOperation = 'lighter'; ctx.lineWidth = Math.max(1, W / 130);
      for (const [ea, eb, ed] of edges) {
        const op = (0.16 + 0.5 * (1 - ed / 0.22)) * (0.55 + 0.45 * Math.sin(t * 1.6 + ea));
        ctx.strokeStyle = `rgba(150,205,255,${op.toFixed(3)})`;
        ctx.beginPath(); ctx.moveTo(P[ea][0], P[ea][1]); ctx.lineTo(P[eb][0], P[eb][1]); ctx.stroke();
      }
      ctx.restore();
      ctx.save(); ctx.globalCompositeOperation = 'lighter';
      nodes.forEach((q, m) => {
        const r = q.sz * (W / 62) * (0.85 + 0.3 * Math.sin(t * 1.8 + q.ph)) * pulse;   // per-node twinkle
        ctx.shadowBlur = W / 4; ctx.shadowColor = q.col; ctx.fillStyle = q.col;
        ctx.beginPath(); ctx.arc(P[m][0], P[m][1], r, 0, 6.2832); ctx.fill();
        ctx.shadowBlur = 0; ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.beginPath(); ctx.arc(P[m][0], P[m][1], r * 0.42, 0, 6.2832); ctx.fill();
      });
      ctx.restore();
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [size]);

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <canvas ref={ref} width={size} height={size} style={{ width: size, height: size }} role="img" aria-label="Stratus" />
      {withText && (
        <div className="leading-tight">
          <div className="font-semibold text-base">Stratus</div>
          <div className="text-[10px] uppercase tracking-widest text-textSecondary">VoIP</div>
        </div>
      )}
    </div>
  );
}

export default StratusLogo;
